import { useCallback, useState, useEffect } from "react";
import { MapStyle, MapStyleId } from "../types";
import { usePreferences } from "./usePreferences";

export function useMap() {
  const { mapPreferences, toggleStarredMap, updateMapPreferences } =
    usePreferences();
  const [isMapLoading, setIsMapLoading] = useState(false);

  // Subscribe to map loading state
  useEffect(() => {
    const service = window.__bikedash_maps?.["main"];
    if (!service) return;

    const unsubscribe = service.onLoadingChange((loading: boolean) => {
      setIsMapLoading(loading);
    });

    return unsubscribe;
  }, []);

  // Use the defaultStyleId from preferences directly
  const currentMapStyleId =
    (mapPreferences.defaultStyleId as MapStyleId) || "osm:standard";

  // Use starred styles from preferences
  const starredStyles = mapPreferences.starredStyleIds as MapStyleId[];

  // Mock map style data
  const getMapStyle = (id: MapStyleId): MapStyle => {
    const styles: Record<MapStyleId, MapStyle> = {
      "osm:standard": {
        id: "osm:standard",
        name: "Standard",
        url: "https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        requiresKey: false,
      },
      "osm:humanitarian": {
        id: "osm:humanitarian",
        name: "Humanitarian",
        url: "https://{a-c}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png",
        requiresKey: false,
      },
      "osm:topographic": {
        id: "osm:topographic",
        name: "Topographic",
        url: "https://{a-c}.tile.opentopomap.org/{z}/{x}/{y}.png",
        requiresKey: false,
      },
      "cartodb:light": {
        id: "cartodb:light",
        name: "Light",
        url: "https://{a-d}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
        requiresKey: false,
      },
      "cartodb:dark": {
        id: "cartodb:dark",
        name: "Dark",
        url: "https://{a-d}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
        requiresKey: false,
      },
      "cartodb:voyager": {
        id: "cartodb:voyager",
        name: "Voyager",
        url: "https://{a-d}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png",
        requiresKey: false,
      },
      "esri:gray": {
        id: "esri:gray",
        name: "Gray",
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
        requiresKey: false,
      },
      "esri:satellite": {
        id: "esri:satellite",
        name: "Satellite",
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        requiresKey: false,
      },
      "thunderforest:cycle": {
        id: "thunderforest:cycle",
        name: "Cycle",
        url: "https://{a-c}.tile.thunderforest.com/cycle/{z}/{x}/{y}.png",
        requiresKey: true,
      },
      "thunderforest:landscape": {
        id: "thunderforest:landscape",
        name: "Landscape",
        url: "https://{a-c}.tile.thunderforest.com/landscape/{z}/{x}/{y}.png",
        requiresKey: true,
      },
      "thunderforest:outdoors": {
        id: "thunderforest:outdoors",
        name: "Outdoors",
        url: "https://{a-c}.tile.thunderforest.com/outdoors/{z}/{x}/{y}.png",
        requiresKey: true,
      },
      "thunderforest:transport": {
        id: "thunderforest:transport",
        name: "Transport",
        url: "https://{a-c}.tile.thunderforest.com/transport/{z}/{x}/{y}.png",
        requiresKey: true,
      },
      "thunderforest:transport-dark": {
        id: "thunderforest:transport-dark",
        name: "Transport Dark",
        url: "https://{a-c}.tile.thunderforest.com/transport-dark/{z}/{x}/{y}.png",
        requiresKey: true,
      },
      "thunderforest:spinal-map": {
        id: "thunderforest:spinal-map",
        name: "Spinal",
        url: "https://{a-c}.tile.thunderforest.com/spinal-map/{z}/{x}/{y}.png",
        requiresKey: true,
      },
      "thunderforest:pioneer": {
        id: "thunderforest:pioneer",
        name: "Pioneer",
        url: "https://{a-c}.tile.thunderforest.com/pioneer/{z}/{x}/{y}.png",
        requiresKey: true,
      },
      "thunderforest:mobile-atlas": {
        id: "thunderforest:mobile-atlas",
        name: "Mobile Atlas",
        url: "https://{a-c}.tile.thunderforest.com/mobile-atlas/{z}/{x}/{y}.png",
        requiresKey: true,
      },
      "thunderforest:neighbourhood": {
        id: "thunderforest:neighbourhood",
        name: "Neighbourhood",
        url: "https://{a-c}.tile.thunderforest.com/neighbourhood/{z}/{x}/{y}.png",
        requiresKey: true,
      },
    };
    return styles[id];
  };

  const currentMapStyle = getMapStyle(currentMapStyleId);

  const cycleMapStyle = useCallback(() => {
    // Check if Thunderforest API key is available
    let hasApiKey = false;
    const cachedAuth = localStorage.getItem("bikedash_cached_auth");
    if (cachedAuth) {
      try {
        const cached = JSON.parse(cachedAuth);
        hasApiKey = !!cached.thunderforestApiKey;
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Filter starred styles to only include available maps
    const availableStyles = starredStyles.filter((styleId) => {
      const style = getMapStyle(styleId);
      // Include map if it doesn't require a key, or if we have a key
      return !style.requiresKey || hasApiKey;
    });

    // If no available styles, do nothing
    if (availableStyles.length === 0) return;

    // Find current style in available styles
    const currentIndex = availableStyles.indexOf(currentMapStyleId);
    const nextIndex = (currentIndex + 1) % availableStyles.length;
    const nextStyleId = availableStyles[nextIndex];

    // Update via preferences which will trigger the map update
    updateMapPreferences({ defaultStyleId: nextStyleId });
  }, [currentMapStyleId, starredStyles, updateMapPreferences]);

  const toggleStarredStyle = useCallback(
    (styleId: MapStyleId) => {
      toggleStarredMap(styleId);
    },
    [toggleStarredMap],
  );

  return {
    currentMapStyle,
    currentMapStyleId,
    starredStyles,
    cycleMapStyle,
    toggleStarredStyle,
    isMapLoading,
  };
}
